import useAppContext from '@groupefungo/react-apollo.core/app/UseContext';
import useCommonFragments from '../../../../../base/hooks/CommonFragments';
// eslint-disable-next-line import/no-cycle
import useOrderFragment from '../../orders/hooks/useOrderFragment';

export default () => {
  const { gql, useQuery, useMutation } = useAppContext();
  const { PageInfoFragment, AddressFragment, InspirationFragment } = useCommonFragments();
  const { OrderFragment } = useOrderFragment();

  const EventForCalendarFragment = gql`
fragment EventForCalendarFragment on Event {
  gid
  budget
  eventCode
  eventTitle
  eventDate
  orderDeadline
  eventType
  numberOfGuests
  daysBeforeEvent
  daysBeforeOrderDeadline
  eventPlanner
  department
  assignedPerson
  status
  statusCategory
  commentsCount
  comments {
    id
    text
    createdAt
    writtenBy {
      email
      firstName
      lastName
      roles {
        id
        name
      }
    }
  }
}  
  `;

  const EventClientFragment = gql`
fragment EventClientFragment on Client {
  email
  firstName
  lastName
}
  `;

  const EventsListFragment = gql`
fragment EventFragment on Event {
  gid
  budget
  eventCode
  eventTitle
  eventDate
  orderDeadline
  eventType
  numberOfGuests
  daysBeforeEvent
  daysBeforeOrderDeadline
  eventPlanner
  department
  assignedPerson
  status
  statusCategory
  commentsCount
  partyMembersCount
}
  `;

  const EventFragment = gql`
fragment EventFragment on Event {
  budget
  eventCode
  eventAppointmentsGids
  eventTitle
  eventDate
  orderDeadline
  eventType
  numberOfGuests
  daysBeforeEvent
  daysBeforeOrderDeadline
  eventPlanner
  department
  assignedPerson
  status
  statusCategory 
  notes 
  comments {
    id
    text
    createdAt
    writtenBy {
      __typename
      email
      firstName
      lastName
    }
  }
  address {
    id
    ...AddressFragment
  }
  clientsEvents {
    id
    gid
    roleName
    orderAttributes {
      ...OrderFragment
    }
    clientAttributes {
      id
      gid
      ...EventClientFragment
    }
  }
  inspirations {
    ...InspirationFragment
  }
}
${EventClientFragment}
${AddressFragment}
${OrderFragment}
${InspirationFragment}
`;

  const EventConnectionGql = gql`
query events($first: Int, $last: Int, $after: String, $before: String, $filters: FiltersInput) {
  events(first: $first, last: $last, after: $after, before: $before, filters: $filters) {
    edges {
      node {
        id
        gid
        ...EventFragment
      }
    }
    pageInfo {
      ...PageInfoFragment
    }
  }
}
${EventsListFragment}
${PageInfoFragment}
`;

  const EventNodeGql = gql`
  query eventNode($gid: ID!) {
    node(gid: $gid) {
      id
      gid
      ...EventFragment
    }
  }
  ${EventFragment}
`;

  const NewEventGql = gql`
query newEvent($clientId: ID) {
  newEvent(clientId: $clientId) {
      eventTitle
      eventDate
      orderDeadline
      eventType
      numberOfGuests
      eventPlanner
      department
      assignedPerson
      status
      statusCategory
      budget
      address {
        ...AddressFragment
      }
      clientsEvents {
        roleName
        clientAttributes {
          ...EventClientFragment
        }
      }
      inspirations {
        ...InspirationFragment
      }
  }
}
${EventClientFragment}
${AddressFragment}
${InspirationFragment}
`;

  const CreateEventMutation = gql`
    mutation createEvent($input: EventInput!) {
      createEvent(input: $input) {
        event {
          id
          gid
          ...EventFragment
        }
      }
    }
  ${EventFragment}
`;

  const UpdateEventMutation = gql`
    mutation updateEvent($input: EventInput!) {
      updateEvent(input: $input) {
        event {
          id
          gid
          ...EventFragment
        }
      }
    }
  ${EventFragment}
`;

  const useEventConnectionQuery = (connection) => {
    const { first, last, before, after, ...filters } = connection;
    const variables = { first, last, before, after, filters };
    return useQuery(EventConnectionGql, {
      variables,
      fetchPolicy: 'network-only',
    });
  };

  const useNewEventQuery = (clientId) => useQuery(NewEventGql, {
    variables: { clientId },
    fetchPolicy: 'network-only',
  });

  const useEventNodeQuery = (gid) => useQuery(EventNodeGql, {
    variables: { gid },
    fetchPolicy: 'network-only',
  });

  const useCreateEventMutation = (input) => useMutation(CreateEventMutation, {
    variables: { input },
  });

  const useUpdateEventMutation = (input) => useMutation(UpdateEventMutation, {
    variables: { input },
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useEventQuery = (gid, clientId = null) => (!gid ? useNewEventQuery(clientId) : useEventNodeQuery(gid));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const useEventMutation = (input) => (input.id ? useUpdateEventMutation(input) : useCreateEventMutation(input));

  return {
    EventForCalendarFragment,
    EventFragment,
    EventClientFragment,
    EventsListFragment,
    useEventConnectionQuery,
    useEventMutation,
    useEventQuery,
    useNewEventQuery,
  };
};
